// src/modules/time-tracking/PayrollReport.js
//
// Admin weekly payroll. Groups completed/auto-closed shifts into Mon–Sun weeks
// per employee (pay = hours × rate) via GET /time-tracking/reports/payroll.
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { format, startOfWeek, endOfWeek, subWeeks } from 'date-fns';
import {
  FaClock, FaDollarSign, FaUsers, FaExclamationTriangle, FaSyncAlt, FaFileCsv,
} from 'react-icons/fa';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import DashboardLayout from '../../layouts/DashboardLayout';
import Select from '../../components/shared/Select';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import { StatusBadge, TableContainer, StyledTable, PageHeader, HeaderContent } from '../../components/styled/CommonStyles';
import { listEmployees } from '../employees/employeeService';
import { getPayrollReport, formatHours } from './timeTrackingService';
import { formatCurrency } from '../../utils/utils';

const mondayWeekStart = (d) => startOfWeek(d, { weekStartsOn: 1 });
const sundayWeekEnd = (d) => endOfWeek(d, { weekStartsOn: 1 });
const iso = (d) => format(d, 'yyyy-MM-dd');
const weekLabel = (isoStr) => format(new Date(isoStr), 'MMM d');

const PERIODS = {
  this_week: { label: 'This week', range: () => ({ from: mondayWeekStart(new Date()), to: sundayWeekEnd(new Date()) }) },
  last_week: { label: 'Last week', range: () => ({ from: mondayWeekStart(subWeeks(new Date(), 1)), to: sundayWeekEnd(subWeeks(new Date(), 1)) }) },
  last_4: { label: 'Last 4 weeks', range: () => ({ from: mondayWeekStart(subWeeks(new Date(), 3)), to: sundayWeekEnd(new Date()) }) },
};

const Filters = styled.div`
  display: flex; flex-wrap: wrap; gap: ${(p) => p.theme.spacing(2)};
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  align-items: flex-end;
`;
const DateField = styled.div`
  display: flex; flex-direction: column; gap: 4px;
  label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small}; }
  input { background: ${(p) => p.theme.colors.background}; color: ${(p) => p.theme.colors.textPrimary};
    border: 1px solid ${(p) => p.theme.colors.inputGray}; border-radius: ${(p) => p.theme.borderRadius};
    padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)}; }
`;
const Chips = styled.div`display: flex; gap: 8px; flex-wrap: wrap;`;
const Chip = styled.button`
  padding: 8px 14px; border-radius: 20px; cursor: pointer; font-weight: 500;
  border: 1px solid ${(p) => (p.active ? p.theme.colors.primary : p.theme.colors.inputGray)};
  background: ${(p) => (p.active ? `${p.theme.colors.primary}22` : 'transparent')};
  color: ${(p) => (p.active ? p.theme.colors.primary : p.theme.colors.textSecondary)};
`;
const Metrics = styled.div`
  display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: ${(p) => p.theme.spacing(2)}; margin-bottom: ${(p) => p.theme.spacing(3)};
`;
const Metric = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; box-shadow: 0 2px 4px rgba(0,0,0,.1);
  .label { color: ${(p) => p.theme.colors.textSecondary}; font-size: ${(p) => p.theme.typography.fontSize.small};
    display: flex; align-items: center; gap: 8px; }
  .value { color: ${(p) => p.$accent || p.theme.colors.primary}; font-size: 2rem; font-weight: 700; margin-top: 6px; }
  svg { color: ${(p) => p.$accent || p.theme.colors.primary}; }
`;
const ChartCard = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
  h3 { color: ${(p) => p.theme.colors.textPrimary}; margin: 0 0 ${(p) => p.theme.spacing(2)}; font-size: 1rem; }
`;
const EmployeeBlock = styled.div`
  background: ${(p) => p.theme.colors.surface}; border-radius: ${(p) => p.theme.borderRadius};
  padding: ${(p) => p.theme.spacing(3)}; margin-bottom: ${(p) => p.theme.spacing(3)};
  box-shadow: 0 2px 4px rgba(0,0,0,.1);
`;
const EmpHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 8px;
  margin-bottom: ${(p) => p.theme.spacing(2)};
  h3 { margin: 0; color: ${(p) => p.theme.colors.textPrimary}; cursor: pointer; }
  h3:hover { color: ${(p) => p.theme.colors.primary}; }
  .totals { color: ${(p) => p.theme.colors.textSecondary}; font-size: 0.9rem;
    strong { color: ${(p) => p.theme.colors.primary}; } }
`;
const Bar2 = styled.button`
  background: ${(p) => p.theme.colors.primary}; color: ${(p) => p.theme.colors.black};
  border: none; border-radius: ${(p) => p.theme.borderRadius}; cursor: pointer;
  padding: ${(p) => p.theme.spacing(1)} ${(p) => p.theme.spacing(2)};
  display: flex; align-items: center; gap: 8px; font-weight: 500;
`;
const GhostBtn = styled(Bar2)`
  background: transparent; color: ${(p) => p.theme.colors.textSecondary};
  border: 1px solid ${(p) => p.theme.colors.inputGray};
`;
const Empty = styled.div`
  text-align: center; padding: ${(p) => p.theme.spacing(5)};
  color: ${(p) => p.theme.colors.textSecondary};
`;

const PayrollReport = () => {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [userId, setUserId] = useState('');
  const [preset, setPreset] = useState('last_4');
  const initial = PERIODS.last_4.range();
  const [from, setFrom] = useState(iso(initial.from));
  const [to, setTo] = useState(iso(initial.to));
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    listEmployees().then(setEmployees).catch(() => setEmployees([]));
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    const { from: f, to: t } = PERIODS[key].range();
    setFrom(iso(f)); setTo(iso(t));
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const toEnd = to ? `${to}T23:59:59` : undefined;
      const res = await getPayrollReport({ userId: userId || undefined, from: from || undefined, to: toEnd });
      setData(res.employees || []);
    } catch (e) {
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [userId, from, to]);

  useEffect(() => { load(); }, [load]);

  // Aggregate KPIs across all returned employees.
  const kpis = useMemo(() => {
    let seconds = 0, pay = 0, unrated = 0;
    for (const emp of data) {
      seconds += emp.totals?.totalSeconds || 0;
      pay += emp.totals?.pay || 0;
      unrated += emp.totals?.unratedShiftCount || 0;
    }
    return { seconds, pay, unrated, employees: data.length };
  }, [data]);

  // Sum hours + pay per week across all employees for the chart.
  const chartData = useMemo(() => {
    const byWeek = new Map();
    for (const emp of data) {
      for (const w of emp.weeks || []) {
        const key = w.weekStart;
        if (!byWeek.has(key)) byWeek.set(key, { week: weekLabel(key), hours: 0, pay: 0, ts: new Date(key).getTime() });
        const row = byWeek.get(key);
        row.hours += w.hours || 0;
        row.pay += w.pay || 0;
      }
    }
    return [...byWeek.values()].sort((a, b) => a.ts - b.ts)
      .map((r) => ({ ...r, hours: Math.round(r.hours * 100) / 100 }));
  }, [data]);

  const exportCsv = () => {
    const rows = [['Employee', 'Week start', 'Week end', 'Hours', 'Pay', 'Shifts', 'Unrated']];
    for (const emp of data) {
      for (const w of emp.weeks || []) {
        rows.push([
          emp.user?.username || `#${emp.user?.id}`,
          format(new Date(w.weekStart), 'yyyy-MM-dd'),
          format(new Date(w.weekEnd), 'yyyy-MM-dd'),
          w.hours, w.pay, w.shiftCount, w.unratedShiftCount || 0,
        ]);
      }
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '')}"`).join(',')).join('\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url; a.download = `payroll_${from}_to_${to}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <DashboardLayout>
      <PageHeader>
        <HeaderContent>
          <h1>Payroll</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <GhostBtn onClick={exportCsv} disabled={!data.length}><FaFileCsv /> Export CSV</GhostBtn>
            <Bar2 onClick={load}><FaSyncAlt /> Refresh</Bar2>
          </div>
        </HeaderContent>
      </PageHeader>

      <Filters>
        <div style={{ minWidth: 200, flex: 1 }}>
          <Select label="Employee" name="userId" value={userId} onChange={(e) => setUserId(e.target.value)}>
            <option value="">All employees</option>
            {employees.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
          </Select>
        </div>
        <DateField><label>From</label><input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setPreset('custom'); }} /></DateField>
        <DateField><label>To</label><input type="date" value={to} onChange={(e) => { setTo(e.target.value); setPreset('custom'); }} /></DateField>
        <Chips>
          {Object.entries(PERIODS).map(([key, p]) => (
            <Chip key={key} active={preset === key} onClick={() => applyPreset(key)}>{p.label}</Chip>
          ))}
        </Chips>
      </Filters>

      <Metrics>
        <Metric><div className="label"><FaClock /> Total hours</div><div className="value">{formatHours(kpis.seconds)}</div></Metric>
        <Metric><div className="label"><FaDollarSign /> Total pay</div><div className="value">{formatCurrency(kpis.pay)}</div></Metric>
        <Metric><div className="label"><FaUsers /> Employees</div><div className="value">{kpis.employees}</div></Metric>
        <Metric $accent={kpis.unrated > 0 ? '#f39c12' : undefined}>
          <div className="label"><FaExclamationTriangle /> Unrated shifts</div>
          <div className="value">{kpis.unrated}</div>
        </Metric>
      </Metrics>

      {loading ? <LoadingSpinner /> : data.length === 0 ? (
        <Empty>No payroll data for this period.</Empty>
      ) : (
        <>
          {chartData.length > 0 && (
            <ChartCard>
              <h3>Hours per week</h3>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                  <XAxis dataKey="week" stroke="#CCCCCC" fontSize={12} />
                  <YAxis stroke="#CCCCCC" fontSize={12} />
                  <Tooltip
                    contentStyle={{ background: '#222', border: '1px solid #444', borderRadius: 8, color: '#fff' }}
                    formatter={(value, name) => (name === 'pay' ? formatCurrency(value) : `${value} h`)}
                  />
                  <Bar dataKey="hours" fill="#F69620" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </ChartCard>
          )}

          {data.map((emp) => (
            <EmployeeBlock key={emp.user?.id}>
              <EmpHeader>
                <h3 onClick={() => navigate(`/time-tracking?userId=${emp.user?.id}`)} title="View shifts">
                  {emp.user?.username || `#${emp.user?.id}`}
                </h3>
                <div className="totals">
                  {formatHours(emp.totals?.totalSeconds)} · <strong>{formatCurrency(emp.totals?.pay || 0)}</strong>
                  {emp.totals?.unratedShiftCount > 0 && (
                    <StatusBadge color="#f39c12" style={{ marginLeft: 8 }}>
                      {emp.totals.unratedShiftCount} unrated
                    </StatusBadge>
                  )}
                </div>
              </EmpHeader>
              <TableContainer>
                <StyledTable>
                  <thead>
                    <tr><th>Week</th><th>Hours</th><th>Pay</th><th>Shifts</th></tr>
                  </thead>
                  <tbody>
                    {emp.weeks.map((w) => (
                      <tr key={w.weekStart}>
                        <td>{weekLabel(w.weekStart)} – {format(new Date(w.weekEnd), 'MMM d')}</td>
                        <td>{formatHours(w.totalSeconds)}</td>
                        <td>{formatCurrency(w.pay)}{w.unratedShiftCount > 0 && ' *'}</td>
                        <td>{w.shiftCount}</td>
                      </tr>
                    ))}
                  </tbody>
                </StyledTable>
              </TableContainer>
            </EmployeeBlock>
          ))}
          {kpis.unrated > 0 && (
            <Empty style={{ padding: 12 }}>* Some shifts have no pay rate set — set the employee's rate to include them.</Empty>
          )}
        </>
      )}
    </DashboardLayout>
  );
};

export default PayrollReport;
